import { useMemo, useState } from 'react';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Icon } from 'Components';
import {
	NavItemConfig,
	bottomNavItems,
	accountItems,
	PEAKPORTAL_KEY,
	getRawPath
} from './Siderbar.constant';
import { useStores } from 'Hooks/useStore';
import NavItem from './components/NavItem';
import RenderNavItem from './components/RenderNavItem';
import UserRow from './components/ProfileSection';
import LogoutModal from 'Pages/App/AppLayout/AppHeader/LogoutModal';
import CustomDropdown from './components/CustomDropDown';
import { APP_ROUTES } from 'Pages/App/App.constants';
import { useCreatePeakV2URL } from 'Hooks';
import { PagesWithDynamicUrl } from 'Hooks/useCreatePeakV2URL';
import useLanguageChange from './hooks/useLanguageChange';
import TenantSelection from './components/TenantSelection';
import styles from './Sidebar.module.scss';

const Sidebar = ({
	isDrawer,
	closeDrawer,
	menuItems
}: {
	isDrawer: boolean;
	closeDrawer: () => void;
	menuItems: NavItemConfig[];
}) => {
	const [collapsed, setCollapsed] = useState(
		localStorage.getItem('menu-collapsed') === 'true'
	);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const [itemActiveKey, setItemActiveKey] = useState('');
	const [isHovered, setIsHovered] = useState(false);
	const [openLanguage, setOpenLanguage] = useState('');
	const history = useHistory();
	const { pathname } = useLocation();
	const { authStore, systemsStore, languageStore } = useStores();
	const { t } = useTranslation();
	const { linkUserToPeakV2URL } = useCreatePeakV2URL({
		page: PagesWithDynamicUrl.Modules
	});
	const { loading, handleLanguageChange } = useLanguageChange({
		authStore,
		languageStore
	});
	const selectedLang = languageStore.language;
	const postFix = authStore.currentUserPoolData?.postfix || '';
	const systemPrefix = ['', APP_ROUTES.HOME].includes(postFix)
		? ''
		: `/${postFix}`;

	const selectedTenantId = systemsStore.selectedTenant.value;
	const isSuperAdmin = authStore.isCurrentUserSuperAdmin;
	const disableOptions = (isSuperAdmin && selectedTenantId === 0);

	const toggleIMGonHover = (val: boolean) => {
		if (collapsed) setIsHovered(val);
	};

	const closeOpenMenu = () => {
		if (itemActiveKey && collapsed) setItemActiveKey('');
	};

	const toggleCollapsedState = () => {
		const value = !collapsed;
		localStorage.setItem('menu-collapsed', `${value}`);
		setCollapsed(value);
	};

	const handleItemClick = async (item: NavItemConfig) => {
		if (item.isExternal) window.open(item.path || '', '_blank');
		else if (!!item?.children?.length) {
			if (itemActiveKey !== item.key) setItemActiveKey(item.key);
			else setItemActiveKey('');
		} else if (item.key === PEAKPORTAL_KEY) {
			const system = systemsStore.getSystemByName(item.label);
			if (system) await linkUserToPeakV2URL(system);
		} else if (typeof item.path === 'string') {
			setItemActiveKey('');
			const path = generatePath(item.path);
			history.push(`${systemPrefix}${path}`);
		}
		if (!item?.children?.length && isDrawer) closeDrawer();
	};

	const handleProfileAction = (item: NavItemConfig) => {
		if (item.key === 'logout') setShowLogoutModal(true);
		else handleItemClick(item);
	};

	const handleLanguageSelection = (item: NavItemConfig) => {
		if (typeof item.path === 'string' && !item.children) {
			if (item.key === 'help') handleItemClick(item);
			else if (item.key !== selectedLang) {
				handleLanguageChange({ key: item.key });
				if (openLanguage) setOpenLanguage('');
			}
		} else if (item.key === 'language' && isDrawer)
			setOpenLanguage('language');
	};

	const { isActiveKey, isOpenKey } = useMemo(() => {
		const items = menuItems.concat(accountItems);
		const leadPath = getRawPath(pathname.replace(systemPrefix, ''));

		for (const item of items) {
			if (item.children?.length) {
				const child = item.children.find((c) => c.path === leadPath);
				if (child) {
					return {
						isActiveKey: child.path || '',
						isOpenKey: itemActiveKey || item.key
					};
				}
			}

			if (item.path === leadPath) {
				return {
					isActiveKey: item.path,
					isOpenKey: itemActiveKey || ''
				};
			}
		}

		return { isActiveKey: '/', isOpenKey: itemActiveKey || '' };
	}, [pathname, itemActiveKey]);

	const appExtraMenus = useMemo(() => {
		const langMenu = (bottomNavItems[0].children || []).find(
			(item) => item.key === selectedLang
		);
		bottomNavItems[0]['label'] = langMenu?.label || 'English';
		return bottomNavItems;
	}, [selectedLang])

	return <>{
		!isDrawer ? <aside className={classNames(styles.sidebar, collapsed ? 'icon-only-view' : '', { [styles['sidebar--collapsed']]: collapsed })}>
			<div className={styles.logo}>
				<div className={styles.logo__icon} onClick={() => history.push(`${systemPrefix}${APP_ROUTES.HOME}`)}>
					<Icon type="logoIcon" role="img" />
				</div>
				{isSuperAdmin ? null : <TenantSelection {...{ isSuperAdmin, systemsStore }} />}
				<div className={styles.logo__collapse} onClick={toggleCollapsedState}
					onMouseEnter={() => toggleIMGonHover(true)} onMouseLeave={() => toggleIMGonHover(false)}>
					<Icon type={(collapsed && !isHovered) ? "logoIcon" : "collapseIcon"} role="img" />
				</div>
			</div>

			{isSuperAdmin ? <TenantSelection {...{ isSuperAdmin, systemsStore }} /> : null}

			{/* Main Nav */}
			<nav className={styles.nav}>
				{menuItems.map((item) => <RenderNavItem {...{ item, isActiveKey, isOpenKey, collapsed, handleItemClick, closeOpenMenu, disableOptions }} key={item.key} />)}
			</nav>

			{/* Bottom Nav */}
			<div className={styles.bottomSection}>
				<div className={styles.bottomNav}>
					{appExtraMenus.map((item) => item.key === "language" ? <CustomDropdown {...{ isActiveKey: selectedLang, handleItemClick: handleLanguageSelection }} parentClass='account-tile' items={item.children || []} TitleComp={null} disableDropdown={loading}>
						<NavItem {...{ item, isActive: false, isOpen: false, hasChildren: true, onClick: () => null, showToolTip: collapsed, loading }} />
					</CustomDropdown> : <NavItem {...{ item, isActive: false, isOpen: false, hasChildren: false, showToolTip: collapsed, onClick: () => handleLanguageSelection(item) }} />)}
				</div>

				{/* User Row */}
				{authStore.currentUser && <UserRow {...{ isActiveKey, collapsed, userDetails: authStore.currentUser }} handleItemClick={handleProfileAction} />}
			</div>
		</aside> : <aside className={classNames(styles.sidebar, styles.sidebarDrawer)}>
			{authStore.currentUser && <UserRow {...{ isActiveKey, collapsed: false, userDetails: authStore.currentUser }} handleItemClick={handleProfileAction} />}
			<div className={styles.mainSection}>
				<div className={styles.section__title}>{t("sidebar.main")}</div>
				<nav className={styles.nav}>
					{menuItems.map((item) => <RenderNavItem {...{ item, isActiveKey, isOpenKey, collapsed: false, handleItemClick, disableOptions }} key={item.key} />)}
					{bottomNavItems.map((item) => <RenderNavItem {...{ item, isActiveKey, isOpenKey: openLanguage, collapsed: false, handleItemClick: handleLanguageSelection, loading: (item.key === "language" && loading), disableOptions: false }} key={item.key} />)}
				</nav>
				<div className={styles.bottomMenu}>
					<div className={styles.section__title}>{t("sidebar.myAccount")}</div>
					{accountItems.map((item) => <RenderNavItem {...{ item, isActiveKey, isOpenKey, collapsed: false, handleItemClick: handleProfileAction, disableOptions: false }} key={item.key} />)}
				</div>
			</div>
		</aside>}
		{showLogoutModal && <LogoutModal onLogout={() => handleItemClick(accountItems[1])} onCancel={() => setShowLogoutModal(false)} />}
	</>
};

export default observer(Sidebar);